import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search, Plus, Phone, MessageSquare, Archive, ChevronRight, Users, FileText } from 'lucide-react';
import WhatsAppIcon from '../components/icons/WhatsAppIcon';
import { Lead, STATUS_CONFIG } from '../types/lead';
import { Tag } from '../types/tag';
import PageHeader from '../components/Layout/PageHeader';
import { useAuth } from '../contexts/AuthContext';

interface LeadsPageProps {
  leads: Lead[];
  tags: Tag[];
  leadTagsMap: Map<string, Tag[]>;
  leadNotesMap: Map<string, string>;
  onLeadClick: (lead: Lead) => void;
  onCreateLead?: () => void;
  onArchiveLead: (leadId: string) => void;
  onDeleteLead: (leadId: string) => void;
}

const LeadsPage: React.FC<LeadsPageProps> = ({
  leads,
  tags,
  leadTagsMap,
  leadNotesMap,
  onLeadClick,
  onCreateLead,
  onArchiveLead,
  onDeleteLead,
}) => {
  const { isKunde } = useAuth();
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const filtered = useMemo(() => {
    return leads.filter(lead => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        lead.name.toLowerCase().includes(q) ||
        lead.email.toLowerCase().includes(q) ||
        lead.phone.includes(q) ||
        (lead.company || lead.meta_data?.company || '').toLowerCase().includes(q);
      const matchStatus = !selectedStatus || lead.status === selectedStatus;
      return matchSearch && matchStatus && !lead.archived;
    });
  }, [leads, search, selectedStatus]);

  const handleCall = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    window.location.href = `tel:${phone.replace(/\s/g, '')}`;
  };

  const handleWhatsApp = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    const clean = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${clean}`, '_blank');
  };

  const handleSMS = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    window.location.href = `sms:${phone.replace(/\s/g, '')}`;
  };

  const handleArchive = (e: React.MouseEvent, leadId: string) => {
    e.stopPropagation();
    onArchiveLead(leadId);
  };

  return (
    <div>
      <PageHeader
        title="Anfragen"
        subtitle={`${filtered.length} Kontakte`}
        actions={onCreateLead ? (
          <button
            onClick={onCreateLead}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Neue Anfrage</span>
          </button>
        ) : undefined}
      />

      <div className="px-4 md:px-8 py-4 space-y-4">
        {/* Search & Filter */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Name, E-Mail, Telefon, Firma..."
              className="w-full pl-9 pr-4 py-2.5 border border-zinc-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            />
          </div>
          <select
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            className="border border-zinc-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900 min-w-[140px]"
          >
            <option value="">Alle Status</option>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
        </div>

        {/* Lead List */}
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">Keine Anfragen gefunden</p>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              <div className="divide-y divide-zinc-100">
                {filtered.map((lead, i) => {
                  const cfg = STATUS_CONFIG[lead.status];
                  const leadTags = leadTagsMap.get(lead.id) || [];
                  const company = lead.company || lead.meta_data?.company;
                  const latestNote = leadNotesMap.get(lead.id);

                  return (
                    <motion.div
                      key={lead.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ delay: Math.min(i * 0.03, 0.3) }}
                      onClick={() => onLeadClick(lead)}
                      className="flex items-center gap-4 px-4 py-3.5 hover:bg-zinc-50 cursor-pointer transition-colors group"
                    >
                      {/* Avatar */}
                      <div className="w-9 h-9 rounded-full bg-zinc-900 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {lead.name.charAt(0).toUpperCase()}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{lead.name}</p>
                          {leadTags.slice(0, 2).map(tag => (
                            <span
                              key={tag.id}
                              className="hidden sm:inline text-xs px-1.5 py-0.5 rounded font-medium"
                              style={{ backgroundColor: tag.color + '20', color: tag.color }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-zinc-400 truncate">
                          {company ? `${company} · ` : ''}{lead.email}
                        </p>
                        {latestNote && (
                          <div className="hidden sm:flex items-center gap-1 mt-1">
                            <FileText className="w-3 h-3 text-amber-400 flex-shrink-0" />
                            <p className="text-xs text-zinc-400 truncate">{latestNote}</p>
                          </div>
                        )}
                      </div>

                      {/* Status Badge */}
                      <span className={`hidden sm:inline-flex text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.bg} ${cfg.color}`}>
                        {cfg.label}
                      </span>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={e => handleCall(e, lead.phone)}
                          className="p-2 rounded-lg hover:bg-emerald-50 text-zinc-400 hover:text-emerald-600 transition-colors"
                          title="Anrufen"
                        >
                          <Phone className="w-4 h-4" />
                        </button>
                        <button
                          onClick={e => handleWhatsApp(e, lead.phone)}
                          className="p-2 rounded-lg hover:bg-green-50 text-zinc-400 hover:text-[#25D366] transition-colors"
                          title="WhatsApp"
                        >
                          <WhatsAppIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={e => handleSMS(e, lead.phone)}
                          className="p-2 rounded-lg hover:bg-blue-50 text-zinc-400 hover:text-blue-500 transition-colors"
                          title="SMS"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        {!isKunde() && (
                          <button
                            onClick={e => handleArchive(e, lead.id)}
                            className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors"
                            title="Archivieren"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <ChevronRight className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                    </motion.div>
                  );
                })}
              </div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadsPage;
